import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  User, 
  Mail, 
  Shield,
  Building2,
  MapPin,
  CreditCard,
  CheckCircle,
  Clock,
  Star,
  Loader2,
  Upload,
  Image,
  Settings
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Company settings
  const [company, setCompany] = useState(null);
  const [companySites, setCompanySites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [changingSite, setChangingSite] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    fetchSubscription();
    if (user?.company_id) {
      fetchCompanyDetails();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await axios.get(`${API}/companies/${user.company_id}`);
      setCompany(response.data);
      setCompanySites(response.data.site_names || [response.data.site_name]);
      setSelectedSite(user.selected_site || user.site_name || '');
    } catch (error) {
      console.error('Failed to fetch company:', error);
    }
  };

  const handleUpgrade = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/subscription/create-checkout`);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPayment(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const logoUrl = reader.result;
        
        // Upload to server
        await axios.post(`${API}/companies/${user.company_id}/logo`, null, {
          params: { logo_url: logoUrl }
        });
        
        setCompany(prev => ({ ...prev, logo_url: logoUrl }));
        toast.success('Company logo updated!');
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploadingLogo(false);
    }
  };

  const handleSiteChange = async (newSite) => {
    setChangingSite(true);
    try {
      await axios.put(`${API}/auth/select-site?site_name=${encodeURIComponent(newSite)}`);
      setSelectedSite(newSite);
      toast.success(`Switched to ${newSite}`);
      // Refresh user context
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch site');
    } finally {
      setChangingSite(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Profile
          </h1>
          <p className="text-muted-foreground mt-1">
            Your account information
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <CardTitle className="font-heading text-xl">{user.name}</CardTitle>
                <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className="mt-1">
                  {user.role === 'admin' ? (
                    <>
                      <Shield className="h-3 w-3 mr-1" />
                      Administrator
                    </>
                  ) : user.role === 'manager' ? (
                    <>
                      <User className="h-3 w-3 mr-1" />
                      Manager
                    </>
                  ) : (
                    <>
                      <User className="h-3 w-3 mr-1" />
                      Operative
                    </>
                  )}
                </Badge>
                {user.admin_status === 'pending_approval' && (
                  <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                    Pending Approval
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs flex items-center gap-2">
                <User className="h-3 w-3" />
                Full Name
              </Label>
              <p className="font-medium">{user.name}</p>
            </div>
            
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs flex items-center gap-2">
                <Mail className="h-3 w-3" />
                Email Address
              </Label>
              <p className="font-medium">{user.email}</p>
            </div>
            
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs flex items-center gap-2">
                <Shield className="h-3 w-3" />
                Role
              </Label>
              <p className="font-medium capitalize">{user.role}</p>
            </div>
          </CardContent>
        </Card>

        {/* Company Settings (Admin only) */}
        {user.company_id && user.role === 'admin' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Company Settings
              </CardTitle>
              <CardDescription>
                Manage your company profile and logo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Company Name */}
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs flex items-center gap-2">
                  <Building2 className="h-3 w-3" />
                  Company Name
                </Label>
                <p className="font-medium text-lg">{user.company_name || company?.name || 'Not set'}</p>
              </div>

              {/* Company Logo */}
              <div className="space-y-3">
                <Label className="text-muted-foreground text-xs flex items-center gap-2">
                  <Image className="h-3 w-3" />
                  Company Logo
                </Label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
                    {company?.logo_url ? (
                      <img 
                        src={company.logo_url} 
                        alt="Company Logo" 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="h-8 w-8 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      ref={logoInputRef}
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="mr-2 h-4 w-4" />
                      )}
                      {company?.logo_url ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Logo will appear as watermark on PDF forms
                    </p>
                  </div>
                </div>
              </div>

              {/* Site Selection */}
              {companySites.length > 1 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs flex items-center gap-2">
                    <MapPin className="h-3 w-3" />
                    Active Site
                  </Label>
                  <Select 
                    value={selectedSite} 
                    onValueChange={handleSiteChange}
                    disabled={changingSite}
                  >
                    <SelectTrigger data-testid="site-selector" className="w-full">
                      {changingSite ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <SelectValue placeholder="Select site" />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {companySites.map(site => (
                        <SelectItem key={site} value={site}>{site}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Switch between sites to view different forms
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Construction Site Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Construction Site
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs flex items-center gap-2">
                <Building2 className="h-3 w-3" />
                Company
              </Label>
              <p className="font-medium">{user.company_name || 'Individual'}</p>
            </div>
            
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs flex items-center gap-2">
                <MapPin className="h-3 w-3" />
                Current Site
              </Label>
              <p className="font-medium">{user.selected_site || user.site_name || 'Not specified'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Status */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSubscription ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : subscription?.has_paid ? (
              <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-800">
                    {subscription.subscription_type === 'lifetime' ? 'Lifetime Access' : 'Monthly Subscription'}
                  </p>
                  <p className="text-sm text-green-700">You have full access to all features</p>
                </div>
                <Badge className="ml-auto bg-green-600">PRO</Badge>
              </div>
            ) : subscription?.trial_active ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-amber-800">Trial Period</p>
                    <p className="text-sm text-amber-700">
                      {subscription.trial_days_left > 0 
                        ? `${subscription.trial_days_left} day${subscription.trial_days_left !== 1 ? 's' : ''} remaining`
                        : 'Ending soon'
                      }
                    </p>
                  </div>
                  <Badge variant="outline" className="border-amber-300 text-amber-700">TRIAL</Badge>
                </div>
                <Button 
                  onClick={handleUpgrade}
                  disabled={processingPayment}
                  className="w-full"
                  data-testid="upgrade-btn"
                >
                  {processingPayment ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Star className="mr-2 h-4 w-4" />
                  )}
                  Upgrade to Pro
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-red-800">Trial Expired</p>
                    <p className="text-sm text-red-700">Upgrade to continue using QA Builder Pro</p>
                  </div>
                </div>
                <Button 
                  onClick={handleUpgrade}
                  disabled={processingPayment}
                  className="w-full bg-primary"
                  data-testid="upgrade-btn"
                >
                  {processingPayment ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="mr-2 h-4 w-4" />
                  )}
                  Upgrade Now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
